import { CaretRightFilled } from "@ant-design/icons";
import { useApproveInfluencer } from "@entities/manageRequests/hooks/useApproveInfluencer";
import { useGetInfluencerRequestFacets } from "@entities/manageRequests/hooks/useGetInfluencerRequestFacets";
import useGetInfluencerRequests from "@entities/manageRequests/hooks/useGetInfluencerRequests";
import { useRejectInfluencer } from "@entities/manageRequests/hooks/useRejectInfluencer";
import InfluencerForm from "@entities/manageRequests/ui/InfluencerForm";
import { SocialMediaInfluencerComp } from "@features/socialMediaInfluencer/SocialMediaInfluencerComp";
import { requestStatusTypeMap } from "@shared/lib/constants";
import { ToastService } from "@shared/lib/services";
import {
  ICreateInfluencerRequest,
  IInfluencerRequestAdd
} from "@shared/lib/types/influencer";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Table, TableTitleLayout } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import {
  Avatar,
  Col,
  Drawer,
  Modal,
  Row,
  Space,
  Tag,
  Typography
} from "antd";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import styles from "./Admin.module.scss";

export function AdminInfluencerRequests() {
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState<number>(1);
  const { facets, isFetching: isFetchingInfluencerFacets } =
    useGetInfluencerRequestFacets();
  const [selectedSocialMediaName, setSelectedSocialMediaName] = useState<
    string[]
  >([]);
  const [selectedAgency, setSelectedAgency] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);

  // const socialMediasToDisplay =
  //   (facets?.socialMediaPlatforms || [])?.map((item) => item._id) || [];

  const statusesToDisplay =
    (facets?.status || []).map((item) => item._id) || [];
  const agenciesToDisplay =
    (facets?.agency || []).map((item) => ({
      value: item.agency?._id,
      name: item.agency?.name,
    })) || [];

  const [isModalVisible, setModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { influencerRequests, isFetching: isFetchingRequests } =
    useGetInfluencerRequests(
      searchQuery,
      selectedSocialMediaName,
      selectedCategory,
      selectedAgency,
      selectedStatus,
      pageSize,
      pageNo
    );

  const { approveInfluencer, isApproving, isApproved } = useApproveInfluencer();
  const { rejectInfluencer, isRejecting, isRejected } = useRejectInfluencer();
  const [openInfluencerDrawer, setOpenInfluencerDrawer] =
    useState<boolean>(false);
  const [selectedInfluencer, setSelectedInfluencer] =
    useState<IInfluencerRequestAdd>({} as IInfluencerRequestAdd);

  useEffect(() => {
    setPageNo(1);
  }, [
    searchQuery,
    selectedSocialMediaName,
    selectedCategory,
    selectedAgency,
    selectedStatus,
  ]);

  useEffect(() => {
    if (isApproved) {
      ToastService.success("Influencer request approved successfully.");
    }
  }, [isApproved]);

  useEffect(() => {
    if (isRejected) {
      ToastService.success("Influencer request rejected.");
    }
  }, [isRejected]);

  // useEffect(() => {}, [sea]);
  const handleApprove = async (
    influencerAgencyRequestId: string | undefined,
    updatedDetails: ICreateInfluencerRequest,
    remarks: string | undefined
  ) => {
    if (influencerAgencyRequestId) {
      approveInfluencer({
        influencerAgencyRequestId,
        updatedDetails,
        remarks,
      });
    }
  };
  const handleReject = async (
    influencerAgencyRequestId: string | undefined,
    remarks: string | undefined
  ) => {
    if (influencerAgencyRequestId) {
      rejectInfluencer({
        influencerAgencyRequestId,
        remarks,
      });
    }
  };

  const handleSocialMediaChange = (value: string[] | undefined) => {
    if (!value) return setSelectedSocialMediaName([]);
    setSelectedSocialMediaName(value);
  };

  useEffect(() => {
    if (isApproved || isRejected) {
      setModalVisible(false);
    }
  }, [isApproved, isRejected]);
  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_: any, record: IInfluencerRequestAdd) => {
        return (
          <Space size={8} style={{ cursor: "pointer" }}>
            <Col>
              {record.firstName} {record.lastName}
            </Col>
          </Space>
        );
      },
    },
    {
      title: "Social Media",
      key: "socialMedia",
      // filterDropdown: (props: { confirm: () => void; close: () => void }) => (
      //   <SelectFilter
      //     placeholder="Select social media"
      //     initialTagCount={3}
      //     itemsToDisplay={socialMediasToDisplay}
      //     selectedItems={selectedSocialMediaName}
      //     handleSelectionChange={handleSocialMediaChange}
      //     props={props}
      //   />
      // ),
      render: (_: any, record: IInfluencerRequestAdd) => {
        return (
          <Row>
            {record.socialMedias?.map((x) => (
              <Col key={x._id} lg={24}>
                <Space>
                  <Avatar src={x.profilePicUrl} />
                  {getPlatformIcon(x.platform)}
                  <Button
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(x.link, "_blank");
                    }}
                    style={{ padding: "0" }}
                  >
                    {x.username}
                  </Button>
                </Space>
              </Col>
            ))}
          </Row>
        );
      },
    },
    {
      title: "Agency",
      key: "agency",
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          placeholder="Select agency"
          initialTagCount={3}
          itemsToDisplay={agenciesToDisplay}
          selectedItems={selectedAgency}
          handleSelectionChange={setSelectedAgency}
          props={props}
        />
      ),
      render: (_: any, record: IInfluencerRequestAdd) => {
        return record.agency.name;
      },
    },
    {
      title: "Category",
      key: "category",
      dataIndex: "category",
      // filterDropdown: (props: { confirm: () => void; close: () => void }) => (
      //   <SelectFilter
      //     placeholder="Select category"
      //     initialTagCount={3}
      //     itemsToDisplay={categoriesToDisplay}
      //     selectedItems={selectedCategory}
      //     handleSelectionChange={setSelectedCategory}
      //     props={props}
      //   />
      // ),
      render: (category: string[]) => {
        return (category || []).map((item: string) => (
          <Tag color={"gray"} style={{ width: "max-content" }}>
            {item}
          </Tag>
        ));
      },
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      minWidth: 150,
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          placeholder="Select status"
          initialTagCount={3}
          itemsToDisplay={statusesToDisplay}
          selectedItems={selectedStatus}
          handleSelectionChange={setSelectedStatus}
          props={props}
        />
      ),
      render: (status: string) => {
        return (
          <Typography.Text
            type={
              requestStatusTypeMap[
                status as keyof typeof requestStatusTypeMap
              ] as React.ComponentProps<typeof Typography.Text>["type"]
            }
          >
            {status}
          </Typography.Text>
        );
      },
    },
    {
      title: "Action",
      key: "actions",
      render: (_: any, record: IInfluencerRequestAdd) => {
        return (
          <Col>
            <Button
              type="link"
              icon={<CaretRightFilled />}
              iconPosition="end"
              onClick={() => {
                if (record.status === "Approved") {
                  setOpenInfluencerDrawer(true);
                } else {
                  setModalVisible(true);
                }
                setSelectedInfluencer(record);
              }}
            >
              Open Request
            </Button>
          </Col>
        );
      },
    },
  ];
  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  const debouncedSearch = useDebouncedCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    250
  );
  return (
    <div>
      <h1 className={styles.title}>Manage Influencer Requests</h1>

        <Table
          columns={columns}
          dataSource={influencerRequests?.data.data}
          loading={isFetchingRequests}
          title={() => <TableTitleLayout 
          totalText={`${influencerRequests?.data.pagination.totalCount || 0} Records found`}
          />}
          rowKey={(record) => record._id}
          scroll={{
            x: "auto",
          }}
          pagination={{
            current: pageNo,
            pageSize: pageSize,
            total: influencerRequests?.data.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
        />
      <Modal
        title={"Manage Influencer Request"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <InfluencerForm
          initialFormData={selectedInfluencer}
          onApprove={handleApprove}
          onReject={handleReject}
          isApproving={isApproving}
          isRejecting={isRejecting}
        />
      </Modal>

      <Drawer
        destroyOnClose
        title={"Influencer"}
        open={openInfluencerDrawer}
        onClose={() => {
          setOpenInfluencerDrawer(false);
        }}
        footer={null}
        width={1300}
      >
        {selectedInfluencer && (
          <SocialMediaInfluencerComp
            creatorInfo={{
              image: selectedInfluencer?.profilePicUrl,
              name: selectedInfluencer?.firstName,
            }}
            socialMediaInfluencerReq={{
              influencerId: selectedInfluencer?.influencerId,
            }}
          />
        )}
      </Drawer>
    </div>
  );
}
